import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { FileExportFormat } from '@epicuro-next/ataraxia/models';
import {
  BASKET_API_TOKEN,
  ClearBasketGQL,
  DeleteBasketGQL,
  EmailBasketGQL,
  GroupedBasketsGQL,
  SubmitBasketGQL,
} from '@epicuro-next/data/baskets';
import {
  BasketPatchModel,
  ExtendedDtoBasket,
} from '@epicuro-next/data/baskets-dto';
import { mapBasketUpdateAccountsRequest } from '@epicuro-next/data/baskets-update';
import {
  INVENTORIES_API_TOKEN,
  Inventory,
  InventoryType,
  ProductInventories,
  productsInventoriesToInventories,
} from '@epicuro-next/data/inventories';
import { ORDERS_API_TOKEN } from '@epicuro-next/data/order-book';
import { extendWithEntityTag } from '@epicuro-next/data/tagged-entities';
import { getCurrentTimeZone } from '@epicuro-next/utilities';
import {
  BasketAccountAddress,
  BasketMappingConfig,
  BasketRowsGroupType,
  BasketStatus,
} from 'graphql-api/baskets-api-interfaces';
import { GroupedBasketsResult } from 'graphql-api/gql-api-interfaces';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';

@Injectable()
export class CBasketService {
  constructor(
    private submitBasketGQL: SubmitBasketGQL,
    private clearBasketGQL: ClearBasketGQL,
    private deleteBasketGQL: DeleteBasketGQL,
    private emailBasketGQL: EmailBasketGQL,
    private groupedBasketsGQL: GroupedBasketsGQL,
    private httpClient: HttpClient,
    @Inject(BASKET_API_TOKEN) private url: string,
    @Inject(ORDERS_API_TOKEN) private ordersUrl: string,
    // TODO: currently we consume one endpoint from inventories for c-basket, should move to GQL layer
    @Inject(INVENTORIES_API_TOKEN) private inventoriesUrl: string,
  ) {
    if (!url) throw new Error('BASKET_API token not provided');
  }

  public getDtoBasket(
    basketId: string,
    ignoreProducts = false,
  ): Observable<ExtendedDtoBasket> {
    let url = `${this.url}/${prepareBasketId(basketId)}`;

    if (ignoreProducts) {
      url += '?returnProducts=false';
    }

    return this.httpClient
      .get<ExtendedDtoBasket>(`${url}`, {
        observe: 'response',
      })
      .pipe(map(extendWithEntityTag));
  }

  public pollOnceDtoBasket(
    basketId: string,
    entityTag?: string | null,
    ignoreProducts = false,
  ): Observable<ExtendedDtoBasket | null> {
    if (!entityTag)
      return throwError(
        () => new Error('No entity tag provided, polling aborted'),
      );

    let url = `${this.url}/${basketId}`;

    if (ignoreProducts) {
      url += '?returnProducts=false';
    }

    return this.httpClient
      .get<ExtendedDtoBasket>(`${url}`, {
        observe: 'response',
        headers: {
          ...(entityTag && { 'If-None-Match': entityTag }),
        },
      })
      .pipe(
        filter((res) => res.headers.get('etag') !== entityTag),
        map((response) => ({
          ...(response.body as ExtendedDtoBasket),
          etag: response.headers.get('etag') || '',
        })),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 304) {
            return of(null);
          }

          return throwError(() => error);
        }),
      );
  }

  public checkBasket(basketId: string): Observable<ExtendedDtoBasket> {
    return this.httpClient
      .post<ExtendedDtoBasket>(
        `${this.ordersUrl}/${basketId}?simulate=true`,
        undefined,
        {
          observe: 'response',
        },
      )
      .pipe(map(extendWithEntityTag));
  }

  public submitBasket(basketId: string) {
    const timezone = getCurrentTimeZone();
    return this.submitBasketGQL.mutate({ basketId, timeZoneId: timezone });
  }

  public createBasket() {
    return this.httpClient
      .post<ExtendedDtoBasket>(
        this.url,
        { basketAccounts: [] },
        {
          observe: 'response',
        },
      )
      .pipe(map(extendWithEntityTag));
  }

  public updateDtoBasket(
    basketPatchModel: BasketPatchModel,
  ): Observable<ExtendedDtoBasket> {
    return this.httpClient
      .patch<ExtendedDtoBasket>(
        `${this.url}/${basketPatchModel.basketHeader.basketId}`,
        basketPatchModel,
        {
          observe: 'response',
          headers: {
            ...(basketPatchModel?.etag && {
              'If-Match': basketPatchModel?.etag,
            }),
          },
        },
      )
      .pipe(map(extendWithEntityTag));
  }

  public updateAccounts(
    basket: ExtendedDtoBasket,
    accountsToAdd: BasketAccountAddress[] = [],
    accountsToRemove: BasketAccountAddress[] = [],
  ) {
    const basketPatchModel = mapBasketUpdateAccountsRequest(
      basket,
      accountsToAdd,
      accountsToRemove,
    );

    return this.httpClient.patch<ExtendedDtoBasket>(
      `${this.url}/${basketPatchModel.basketHeader.basketId}`,
      basketPatchModel,
      {
        headers: {
          ...(basket?.etag && { 'If-Match': basket?.etag }),
        },
      },
    );
  }

  public clearBasket(basketId: string, status: BasketStatus) {
    if (status === BasketStatus.New) {
      return this.clearBasketGQL.mutate({ basketId });
    } else {
      return throwError(() => new Error());
    }
  }

  public deleteBasket(basketId: string) {
    return this.deleteBasketGQL.mutate({ basketId });
  }

  public reuseBasket(basketId: string, accountId?: string) {
    let url = `${this.url}/${basketId}/reuse`;

    if (accountId) {
      url += `/${accountId}`;
    }

    return this.httpClient
      .post<ExtendedDtoBasket>(url, {})
      .pipe(catchError((e) => this.handleCopyBasketError(e)));
  }

  public moveBasket(basketId: string, accountId: string) {
    return this.httpClient
      .post<ExtendedDtoBasket>(
        `${this.url}/${basketId}/reuse/${accountId}?preserveOriginalBasket=true`,
        {},
        {
          observe: 'response',
        },
      )
      .pipe(
        map(extendWithEntityTag),
        catchError((e) => this.handleCopyBasketError(e)),
      );
  }

  private handleCopyBasketError(e: HttpErrorResponse) {
    const noBasketItemsErrorMsg = 'No basket items are available';
    const noItemsError = e.error?.message?.includes(noBasketItemsErrorMsg);

    if (noItemsError) {
      return throwError(() => new Error(noBasketItemsErrorMsg));
    }

    return throwError(() => e.error);
  }

  public mergeBaskets(basketIds: string[]) {
    return this.httpClient
      .post<ExtendedDtoBasket>(`${this.url}/merge`, basketIds, {
        observe: 'response',
      })
      .pipe(map(extendWithEntityTag));
  }

  public emailBasket(
    displayName: string,
    email: string,
    message: string,
    basketId: string,
  ) {
    return this.emailBasketGQL.mutate({
      basketId,
      request: { message, recipients: [{ email, displayName }] },
    });
  }

  public getGroupedBaskets(
    basketId: string,
    groupsBy: BasketRowsGroupType[],
    settings: BasketMappingConfig,
  ) {
    return this.groupedBasketsGQL
      .fetch({
        basketId,
        groupsBy,
        settings,
      })
      .pipe(
        map((result) => {
          return result.data as GroupedBasketsResult;
        }),
      );
  }

  public exportBasket(
    basketId: string,
    format: FileExportFormat = FileExportFormat.XLSX,
  ) {
    return this.httpClient.get(
      `${this.url}/${basketId}/export?format=${format}`,
      { responseType: 'blob', observe: 'response' },
    );
  }

  // TODO: currently we consume one endpoint from inventories for c-basket, should move to GQL layer
  public getInventories(
    accountId: string,
    brandId: string,
    productId: string,
    materialId: string | null,
    inventoryType: InventoryType = InventoryType.all,
  ): Observable<Inventory[]> {
    return this.httpClient
      .get<ProductInventories[]>(
        `${this.inventoriesUrl}/${accountId}/${brandId}/${productId}?inventoryType=${inventoryType}`,
      )
      .pipe(map(productsInventoriesToInventories(productId, materialId)));
  }

  public downloadBasketPdfAsync(basketId: string) {
    return this.httpClient.get(`${this.url}/${basketId}/pdf`);
  }

  public sendPdfConfirmation(basketId: string) {
    return this.httpClient
      .post<void>(`${this.url}/${basketId}/resend-confirmation`, {
        timeZoneId: getCurrentTimeZone(),
      })
      .pipe(
        catchError(() =>
          throwError(() => new Error('Failed to send PDF confirmation')),
        ),
      );
  }

  public updateManualDiscounts(
    basketId: string,
    discounts: { basketItemId: string; value: number | null }[],
  ) {
    const payload: {
      itemId: string;
      discount: number | null;
    }[] = discounts.map((discount) => {
      return {
        itemId: discount.basketItemId,
        discount: discount.value,
      };
    });

    return this.httpClient.post<string>(
      `${this.url}/${basketId}/discounts`,
      payload,
    );
  }

  public deleteManualDiscounts(basketId: string, basketItemIds: string[]) {
    return this.httpClient.delete<string>(
      `${this.url}/${basketId}/discounts/items`,
      { body: basketItemIds },
    );
  }
}

// using etag leads to use the same url in request
// so for basket with id 1234-56 BE returns same basket with other etags for GET /123456 and GET /1234-56 requests
function prepareBasketId(basketId: string) {
  return basketId.replace(/-/g, '');
}
